import React, { useState, useMemo } from 'react';
import MeetingCard from './MeetingCard';
import { useAuth } from '../../../contexts/AuthContext';
import { STATUS_CONFIG, MEETING_TEMPLATES } from '../utils/meetingTemplates';
import { arMittMote, SER_ALLA_MOTEN } from '../utils/mittMote';

const MeetingList = ({
  meetings: allaMoten,
  loading,
  onMeetingClick,
  onReschedule,
  onDelete,
  onAddNote,
  onOpenProtocol,
  fokusMoteId = null,
  notesCounts = {}
}) => {
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState({});
  const [typeFilter, setTypeFilter] = useState({});
  const [visaAlla, setVisaAlla] = useState(false);

  // BK, IA och admin får alla projektets möten. Förvalt visas bara de egna, så
  // listan är lika ren som förut; "Alla" tar fram övrigas möten och deras
  // protokoll. Ett möte som öppnats via länk (?protokoll=) visas alltid.
  const kanValjaOmfang = SER_ALLA_MOTEN.includes(user?.role);
  const andrasAntal = useMemo(
    () => (kanValjaOmfang ? allaMoten.filter(m => !arMittMote(m, user)).length : 0),
    [allaMoten, kanValjaOmfang, user]
  );
  const meetings = useMemo(() => {
    if (!kanValjaOmfang || visaAlla) return allaMoten;
    return allaMoten.filter(m => arMittMote(m, user) || m._id === fokusMoteId);
  }, [allaMoten, kanValjaOmfang, visaAlla, user, fokusMoteId]);

  // Räkna antal per status
  const statusCounts = useMemo(() => {
    const counts = {};
    meetings.forEach(m => {
      counts[m.status] = (counts[m.status] || 0) + 1;
    });
    return counts;
  }, [meetings]);

  // Räkna antal per typ
  const typeCounts = useMemo(() => {
    const counts = {};
    meetings.forEach(m => {
      counts[m.type] = (counts[m.type] || 0) + 1;
    });
    return counts;
  }, [meetings]);

  // Kolla om något filter är aktivt
  const hasActiveStatusFilter = Object.values(statusFilter).some(v => v);
  const hasActiveTypeFilter = Object.values(typeFilter).some(v => v);

  // Filtrera möten
  const filteredMeetings = useMemo(() => {
    return meetings.filter(meeting => {
      // Om inget statusfilter är aktivt, visa alla
      if (hasActiveStatusFilter && !statusFilter[meeting.status]) return false;
      // Om inget typfilter är aktivt, visa alla
      if (hasActiveTypeFilter && !typeFilter[meeting.type]) return false;
      return true;
    });
  }, [meetings, statusFilter, typeFilter, hasActiveStatusFilter, hasActiveTypeFilter]);

  // Gruppera möten efter status
  const groupedMeetings = useMemo(() => {
    const upcoming = filteredMeetings.filter(m =>
      m.status === 'scheduled' && new Date(m.scheduledAt) >= new Date()
    ).sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));

    const pending = filteredMeetings.filter(m =>
      m.status === 'draft' || m.status === 'proposed'
    ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const past = filteredMeetings.filter(m =>
      m.status === 'completed' ||
      m.status === 'cancelled' ||
      (m.status === 'scheduled' && new Date(m.scheduledAt) < new Date())
    ).sort((a, b) => new Date(b.scheduledAt || b.createdAt) - new Date(a.scheduledAt || a.createdAt));

    return { upcoming, pending, past };
  }, [filteredMeetings]);

  // Toggle status filter
  const toggleStatusFilter = (status) => {
    setStatusFilter(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  // Toggle type filter
  const toggleTypeFilter = (type) => {
    setTypeFilter(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Återställ filter
  const resetFilters = () => {
    setStatusFilter({});
    setTypeFilter({});
  };

  // Status badges config
  const statusBadges = [
    { status: 'draft', activeClass: 'bg-gray-100 text-gray-800 border border-gray-400' },
    { status: 'proposed', activeClass: 'bg-yellow-50 text-yellow-800 border border-yellow-400' },
    { status: 'scheduled', activeClass: 'bg-blue-50 text-blue-800 border border-blue-400' },
    { status: 'completed', activeClass: 'bg-green-50 text-green-800 border border-green-400' },
    { status: 'cancelled', activeClass: 'bg-red-50 text-red-800 border border-red-400' },
  ];

  // Type badges config
  const typeBadges = [
    { type: 'projektgenomgang', activeClass: 'bg-violet-50 text-violet-800 border border-violet-400' },
    { type: 'efter_rivning', activeClass: 'bg-orange-50 text-orange-800 border border-orange-400' },
    { type: 'byggmote', activeClass: 'bg-blue-50 text-blue-800 border border-blue-400' },
    { type: 'slutsyn', activeClass: 'bg-green-50 text-green-800 border border-green-400' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Finns det badges att visa?
  const hasStatusesToShow = Object.keys(statusCounts).length > 0;
  const hasTypesToShow = Object.keys(typeCounts).length > 0;
  const hasAnyFilter = hasActiveStatusFilter || hasActiveTypeFilter;

  return (
    <div className="space-y-6">
      {/* Mina möten / Alla — bara när det finns andras möten att visa */}
      {kanValjaOmfang && andrasAntal > 0 && (
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-sm">
          {[{ alla: false, label: 'Mina möten' }, { alla: true, label: `Alla (${allaMoten.length})` }].map(val => (
            <button
              key={val.label}
              onClick={() => setVisaAlla(val.alla)}
              className={`px-3 py-1 rounded-md transition-colors ${
                visaAlla === val.alla ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {val.label}
            </button>
          ))}
        </div>
      )}

      {/* Filter-badges */}
      {(hasStatusesToShow || hasTypesToShow) && (
        <div className="space-y-3">
          {/* Status-filter */}
          {hasStatusesToShow && (
            <div className="flex flex-col">
              <div className="mb-1 text-sm text-gray-600">Status</div>
              <div className="flex flex-wrap gap-2 items-center">
                {statusBadges
                  .filter(({ status }) => statusCounts[status] > 0)
                  .map(({ status, activeClass }) => (
                    <button
                      key={status}
                      onClick={() => toggleStatusFilter(status)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        statusFilter[status]
                          ? activeClass
                          : 'bg-gray-50 text-gray-500 border border-gray-200 hover:border-primary/50'
                      }`}
                    >
                      {STATUS_CONFIG[status]?.label || status} ({statusCounts[status]})
                    </button>
                  ))}
                {hasAnyFilter && (
                  <button
                    onClick={resetFilters}
                    className="ml-2 text-xs text-gray-500 hover:text-primary flex items-center gap-1 px-2 py-0.5 rounded hover:bg-gray-100"
                    title="Återställ alla filter"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Återställ
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Typ-filter */}
          {hasTypesToShow && (
            <div className="flex flex-col">
              <div className="mb-1 text-sm text-gray-600">Mötestyp</div>
              <div className="flex flex-wrap gap-2 items-center">
                {typeBadges
                  .filter(({ type }) => typeCounts[type] > 0)
                  .map(({ type, activeClass }) => (
                    <button
                      key={type}
                      onClick={() => toggleTypeFilter(type)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        typeFilter[type]
                          ? activeClass
                          : 'bg-gray-50 text-gray-500 border border-gray-200 hover:border-primary/50'
                      }`}
                    >
                      {MEETING_TEMPLATES[type]?.label || type} ({typeCounts[type]})
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tom-tillstånd */}
      {filteredMeetings.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="text-gray-600 font-medium mb-1">
            {hasAnyFilter
              ? 'Inga möten matchar filtren'
              : 'Inga möten ännu'
            }
          </h3>
          <p className="text-gray-500 text-sm">
            {!hasAnyFilter
              ? 'Klicka på "Nytt möte" för att skapa det första mötet.'
              : 'Prova att ändra filter för att se fler möten.'
            }
          </p>
        </div>
      )}

      {/* Väntar på åtgärd - viktigast först */}
      {groupedMeetings.pending.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
            Väntar på åtgärd ({groupedMeetings.pending.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groupedMeetings.pending.map(meeting => (
              <MeetingCard
                key={meeting._id}
                meeting={meeting}
                onClick={onMeetingClick}
                onReschedule={onReschedule}
                onDelete={onDelete}
                onAddNote={onAddNote}
                onOpenProtocol={onOpenProtocol}
                notesCount={notesCounts[meeting._id] || 0}
              />
            ))}
          </div>
        </div>
      )}

      {/* Kommande möten */}
      {groupedMeetings.upcoming.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
            Kommande möten ({groupedMeetings.upcoming.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groupedMeetings.upcoming.map(meeting => (
              <MeetingCard
                key={meeting._id}
                meeting={meeting}
                onClick={onMeetingClick}
                onReschedule={onReschedule}
                onDelete={onDelete}
                onAddNote={onAddNote}
                onOpenProtocol={onOpenProtocol}
                notesCount={notesCounts[meeting._id] || 0}
              />
            ))}
          </div>
        </div>
      )}

      {/* Tidigare möten */}
      {groupedMeetings.past.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
            Tidigare möten ({groupedMeetings.past.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groupedMeetings.past.map(meeting => (
              <MeetingCard
                key={meeting._id}
                meeting={meeting}
                onClick={onMeetingClick}
                onReschedule={onReschedule}
                onDelete={onDelete}
                onAddNote={onAddNote}
                onOpenProtocol={onOpenProtocol}
                notesCount={notesCounts[meeting._id] || 0}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingList;
